/**
 * useAdminCopilot — hook orchestration per AI Ops Co-pilot admin.
 *
 * Gestisce:
 *  - Sessione corrente (id persistito in localStorage, sopravvive reload)
 *  - Caricamento messaggi history quando si seleziona una sessione esistente
 *  - Send message + ottimistic append user message + assistant response
 *  - Lista sessioni (history)
 *  - Reset (apri nuova chat)
 *
 * Pattern stateful in-memory (no Zustand) perché il panel è singleton
 * nell'AdminLayout — non serve cross-component sync.
 *
 * Sprint admin_ai_copilot S5 (28/05/26).
 */
import { useCallback, useEffect, useState } from 'react';

import { adminApi } from '@/services/adminApi';

const STORAGE_KEY = 'hiromatch.admin.copilot.activeSessionId';

export interface CopilotMessage {
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  statusMessages?: string[];
  tokenUsage?: { promptTokens: number; completionTokens: number; totalTokens: number };
  createdAt: string;
  /** Solo lato FE: true se è un placeholder ottimistico in attesa di server response */
  pending?: boolean;
}

export interface CopilotSessionSummary {
  sessionId: string;
  createdAt: string;
  lastMessageAt: string;
  messageCount: number;
  totalTokens: number;
  title: string | null;
}

export interface UseAdminCopilotResult {
  /** sessionId attiva (null se nuova chat non ancora inviata) */
  sessionId: string | null;
  /** messaggi della session attiva (ordinati cronologici asc) */
  messages: CopilotMessage[];
  /** sending: true mentre aspettiamo response dell'AI */
  sending: boolean;
  /** loadingHistory: true mentre carichiamo messaggi di una session esistente */
  loadingHistory: boolean;
  /** errore ultimo invio */
  error: string | null;
  /** invia un messaggio. Se sessionId è null, ne crea una nuova lato BE. */
  send: (message: string) => Promise<void>;
  /** apri una sessione esistente (carica history) */
  openSession: (sessionId: string) => Promise<void>;
  /** apri una nuova chat (clear messaggi + sessionId) */
  newChat: () => void;
  /** lista sessioni recenti del sudo corrente */
  sessions: CopilotSessionSummary[];
  /** carica la lista sessioni dal BE */
  loadSessions: () => Promise<void>;
  /** loading flag sessions */
  loadingSessions: boolean;
}

export function useAdminCopilot(): UseAdminCopilotResult {
  const [sessionId, setSessionId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<CopilotSessionSummary[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Persist sessionId in localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionId) window.localStorage.setItem(STORAGE_KEY, sessionId);
    else window.localStorage.removeItem(STORAGE_KEY);
  }, [sessionId]);

  // Auto-carica history quando montiamo con un sessionId esistente
  useEffect(() => {
    if (!sessionId || messages.length > 0) return;
    let cancelled = false;
    setLoadingHistory(true);
    adminApi.copilot
      .listMessages(sessionId)
      .then((res) => {
        if (cancelled) return;
        setMessages(res.items);
      })
      .catch((err) => {
        if (cancelled) return;
        // Se 404, la sessione persistita in localStorage non è più valida → reset
        if ((err as Error).message?.includes('404')) {
          setSessionId(null);
          setMessages([]);
        } else {
          setError((err as Error).message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const send = useCallback(
    async (message: string) => {
      if (!message.trim() || sending) return;
      setError(null);
      setSending(true);

      // Ottimistic: append user message subito
      const userPlaceholder: CopilotMessage = {
        messageId: `temp-${Date.now()}`,
        role: 'user',
        content: message,
        createdAt: new Date().toISOString(),
        pending: true,
      };
      setMessages((prev) => [...prev, userPlaceholder]);

      try {
        const res = await adminApi.copilot.chat(message, sessionId ?? undefined);
        // Server assegnerà definitivo sessionId se è la prima
        if (!sessionId) setSessionId(res.sessionId);

        setMessages((prev) => {
          // Sostituisci placeholder user + appendi assistant
          const withoutPlaceholder = prev.filter((m) => m.messageId !== userPlaceholder.messageId);
          return [
            ...withoutPlaceholder,
            // user message confermato (no messageId dal server ma è già persistito)
            { ...userPlaceholder, pending: false },
            {
              messageId: res.messageId,
              role: 'assistant',
              content: res.text,
              statusMessages: res.statusMessages,
              ...(res.tokenUsage ? { tokenUsage: res.tokenUsage } : {}),
              createdAt: new Date().toISOString(),
            },
          ];
        });
      } catch (err) {
        const msg = (err as Error).message || 'Errore invio';
        setError(msg);
        // Rimuovi placeholder user — la chat non è andata a buon fine
        setMessages((prev) => prev.filter((m) => m.messageId !== userPlaceholder.messageId));
      } finally {
        setSending(false);
      }
    },
    [sending, sessionId],
  );

  const openSession = useCallback(async (newSessionId: string) => {
    setSessionId(newSessionId);
    setMessages([]); // trigger auto-load via useEffect
    setError(null);
  }, []);

  const newChat = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setError(null);
  }, []);

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await adminApi.copilot.listSessions(50);
      setSessions(res.items);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  return {
    sessionId,
    messages,
    sending,
    loadingHistory,
    error,
    send,
    openSession,
    newChat,
    sessions,
    loadSessions,
    loadingSessions,
  };
}
